import { B2Button, B2Tabs } from "@hdb/biome-2";
import { useQuery } from "@tanstack/react-query";
import { FC, createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDeviceStatusCount } from "src/core/api/services/dms/device.services";
import TitleComponent from "src/shared/title/title.component";
import { getAllFloorsByBlockCode } from "src/store/slices/floor.slice";
import * as yup from "yup";
import axiosInstance from "../../../../../../src/core/api/axiosClientDMS";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import ProgressBarModalComponent from "../../../../../core/components/modal/progress-bar-modal.component";
import TableComponent from "../../../../../core/components/table/table.component";
import SkeletonComponent from "../../../../../shared/custom-spinner/skeleton.component";
import FilterFormComponent, {
  FilterFormConfig,
} from "../../../../../shared/filter-form/filter-form.component";
import {
  addDevice,
  deleteDevice,
  editDevice,
  getDMSDeviceById,
  getDMSDeviceList,
  setFilter
} from "../../../../../store/slices/dms-device.slice";
import { getBlocksBySiteCode } from "../../../../../store/slices/location-block.slice";
import { getSiteByTCCode } from "../../../../../store/slices/location-site.slice";
import { getTownCouncils } from "../../../../../store/slices/town-council.slice";
import "../../../admin.scss";
import AssetEditTabComponent from "../../inventory/components/asset-edit-tab.component";
import AssetUploadModal from "../../inventory/components/asset-upload-modal.component";
import SearchBarComponent from "../../search-bar/search-bar.component";
import DeviceStatisticsSection from "./device-statistics-section.component";

interface DMSDeviceTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  filter: any;
  onRefetchData: (
    tcCode?: any,
    siteCode?: any,
    blockCode?: any,
    isOnline?: any,
    isActive?: any
  ) => void;
  isLoading: boolean;
  onSearch: (val: string) => void;
  dmsDeviceSearchValue: string;
  sortBy: string;
  sortDirection: string;
}

const DMSDeviceTableComponent: FC<DMSDeviceTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  filter,
  onRefetchData,
  isLoading,
  onSearch,
  dmsDeviceSearchValue,
  sortBy,
  sortDirection,
}) => {
  const formRef = createRef();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [selectedIdToDeleteOrEdit, setSelectedIdToDeleteOrEdit] =
    useState<number>(0);
  const [tcOptions, setTCOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [blockOptions, setBlockOptions] = useState<any>([]);
  const [progress, setProgress] = useState<number>(0);
  const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
  // const [initialValues, setInitialValues] = useState<any>({
  //   deviceCode: "",
  //   tcCode: "",
  //   siteCode: "",
  //   blockCode: "",
  //   registrationTimeStamp: "",
  //   status: "",
  //   firstActiveTimeStamp: "",
  //   lastDataRecievedtimestamp: "",
  // });

  const [selectedTcCode, setSelectedTcCode] = useState(null);
  const [selectedSiteCode, setSelectedSiteCode] = useState(null);
  const [selectedBlockCode, setSelectedBlockCode] = useState(null);

  // const [downloaDocumentPayload, setDownloaDocumentPayload] = useState<any>({
  //   townCouncil: "",
  //   site: "",
  //   block: "",
  //   isOnline: "",
  // });

  const [isSubmitedFilter, setIsSubmitedFilter] = useState(0);

  const onlineOfflineOptions = [
    {
      label: "Online",
      value: "Online",
    },
    {
      label: "Offline",
      value: "Offline",
    },
  ];

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
  }, [dispatch]);

  const townCouncilData = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const isLoadingTownCouncils = useSelector((state: any) => {
    return state.townCouncil.isLoadingTownCouncils;
  });

  const siteDataByTCCode = useSelector((state: any) => {
    return state.locationSite.sitesByTCCode;
  });

  const isLocationSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.isLocationSitesByTCCode;
  });

  const blockDataSiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  const selectedItems = useSelector((state: any) => state.table.selectedItems);

  const isLoadingBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.isLoadingBlocksBySiteCode;
  });
  const [currentTC, setCurrentTC] = useState({ label: "", value: "" });

  useEffect(() => {
    const tcs = townCouncilData?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));

    if (!tcs) return;

    // if user is TC Admin only 1 tc is available
    if (tcs?.length === 1) {
      setCurrentTC(tcs[0]);
    }

    setTCOptions(tcs);
    setFilterUpdateCount(filterUpdateCount + 1);
  }, [townCouncilData]);

  useEffect(() => {
    if (currentTC.value.length) {
      setFormFilterInitialValues((prev) => ({
        ...prev,
        townCouncil: currentTC.value,
      }));

      dispatch(getSiteByTCCode({ tcCode: currentTC.value }));
    }
  }, [currentTC]);

  useEffect(() => {
    const sites = siteDataByTCCode?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    if (!sites?.length) return;

    setSiteOptions(sites);
  }, [siteDataByTCCode]);

  useEffect(() => {
    const blocks = blockDataSiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    if (!blocks) return;

    setBlockOptions(blocks);
  }, [blockDataSiteCode]);

  const onChangeTC = (selectedTC: any) => {
    setSiteOptions([]);
    setBlockOptions([]);

    setSelectedTcCode(selectedTC);
    setSelectedSiteCode(null);
    setSelectedBlockCode(null);

    const form: any = formRef.current;

    if (form) {
      // Get all field names except 'towncouncil'
      const fieldNames = Object.keys(form.values).filter(
        (name) => name !== "towncouncil"
      );

      // Reset each field to empty, except 'towncouncil'
      fieldNames.forEach((fieldName) => {
        if (fieldName === "site" || fieldName === "block") {
          form.setFieldValue(fieldName, "", false); // false to avoid validation after setting field
        }
      });
    }

    setFormFilterInitialValues({
      ...formFilterInitialValues,
      townCouncil: selectedTC,
      site: "",
      block: "",
    });

    dispatch(getSiteByTCCode({ tcCode: selectedTC }));
  };

  const onChangeSite = (selectedSite: any) => {
    setBlockOptions([]);

    setSelectedSiteCode(selectedSite);
    setSelectedBlockCode(null);

    setFormFilterInitialValues({
      ...formFilterInitialValues,
      site: selectedSite,
      block: "",
    });

    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));
  };

  const onChangeBlock = (selectedBlock: any) => {
    setSelectedBlockCode(selectedBlock);
  };

  const onSubmitFilter = () => {
    const form: any = formRef.current;

    // const { townCouncil, site, block, isOnline } = form.values;
    const { townCouncil, site, block } = form.values;

    if (form) {
      form.handleSubmit();

      if (!townCouncil) return;

      // setDownloaDocumentPayload({
      //   townCouncil,
      //   site,
      //   block,
      //   isOnline,
      // });

      if (townCouncil || site || block) {
        dispatch(setFilter(form.values));
      }

      setIsSubmitedFilter(isSubmitedFilter + 1);
    }
  };

  /* const onClickExportDevice = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();

      let { townCouncil, site, block, isOnline } = downloaDocumentPayload;

      if (!townCouncil) {
        toast.warning("Please filter first then Export", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnFocusLoss: true,
          draggable: true,
          pauseOnHover: true,
        });

        return;
      } else if (
        (selectedTcCode && townCouncil && selectedTcCode != townCouncil) ||
        (selectedSiteCode && site && selectedSiteCode != site) ||
        (selectedBlockCode && block && selectedBlockCode != block)
      ) {
        toast.warning("Please filter with the new selections first", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnFocusLoss: true,
          draggable: true,
          pauseOnHover: true,
        });

        return;
      }

      let isOnlineAsset = isOnline
        ? isOnline == "false"
          ? false
          : true
        : null;

      dispatch(
        exportDevices({
          tcCode: townCouncil ? townCouncil : null,
          siteCode: site ? site : null,
          blockCode: block ? block : null,
          isActive: false,
          isOnline: isOnlineAsset,
          searchText: dmsDeviceSearchValue,
          sortBy: sortBy,
          sortDirection: sortDirection,
        })
      );
    }
  }; */

  const onReset = () => {
    const form: any = formRef.current;
    form.resetForm();
    setFilterUpdateCount(filterUpdateCount + 1);

    setFormFilterInitialValues({
      townCouncil: "",
      site: "",
      block: "",
      isOnline: "",
    });
    dispatch(setFilter({}));
    setSiteOptions([]);
    setBlockOptions([]);

    setSelectedTcCode(null);
    setSelectedSiteCode(null);
    setSelectedBlockCode(null);

    setIsSubmitedFilter(0);
  };

  const dmsDeviceDetails = useSelector((state: any) => {
    return state.dmsDevice.dmsDeviceDetails;
  });

  const isLoadingDmsDeviceDetails = useSelector((state: any) => {
    return state.dmsDevice.isLoadingDmsDeviceDetails;
  });

  /*   const isLoadingDeviceExport = useSelector((state: any) => {
      return state.dmsDevice.isLoadingDeviceExport;
    }); */

  const handleCloseModal = () => {
    setIsAssetModalOpen(false);
    setIsModalOpen(false);
    setIsEdit(false);
    setIsDelete(false);

    setFileUploadResponse([]);
    setEditableRows([]);

    // setInitialValues({
    //   deviceCode: "",
    //   tcCode: "",
    //   siteCode: "",
    //   blockCode: "",
    //   registrationTimeStamp: "",
    //   status: "",
    //   firstActiveTimeStamp: "",
    //   lastDataRecievedtimestamp: "",
    // });
  };

  const editAction = (id: number) => {
    setIsEdit(true);
    setIsModalOpen(true);
    setIsDelete(false);

    dispatch(getDMSDeviceById({ deviceId: id }))
      .then((data: any) => {
        void Promise.all([
          dispatch(getSiteByTCCode({ tcCode: data?.payload?.tcCode })),
          dispatch(getBlocksBySiteCode({ siteCode: data?.payload?.siteCode })),
          dispatch(getAllFloorsByBlockCode({ blockCode: data?.payload?.blockCode })),
        ]);
      })
      .catch((error: any) => {
        console.log("Error in api call: ", error);
      });

    setSelectedIdToDeleteOrEdit(id);
  };

  const deleteAction = (id: number) => {
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(true);

    setSelectedIdToDeleteOrEdit(id);
  };

  const handleDelete = () => {
    dispatch(deleteDevice({ id: selectedIdToDeleteOrEdit })).then(() => {
      onRefetchData();
      handleCloseModal();
    });
  };

  // useEffect(() => {
  //   if (!dmsDeviceDetails || !Object.keys(dmsDeviceDetails).length) return;

  //   // setInitialValues({
  //   //   deviceCode: dmsDeviceDetails?.deviceCode || "",
  //   //   tcCode: dmsDeviceDetails?.tcCode || "",
  //   //   siteCode: dmsDeviceDetails?.siteCode || "",
  //   //   blockCode: dmsDeviceDetails?.blockCode || "",
  //   //   registrationTimeStamp: dmsDeviceDetails?.registrationTimeStamp || "",
  //   //   status: dmsDeviceDetails?.status || "",
  //   //   firstActiveTimeStamp: dmsDeviceDetails?.firstActiveTimeStamp || "",
  //   //   lastDataRecievedtimestamp:
  //   //     dmsDeviceDetails?.lastDataRecievedtimestamp || "",
  //   // });
  // }, [dmsDeviceDetails]);

  const onSubmit = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();
      if (isEdit) {
        const formValues = form.values;
        const payload = {
          ...formValues,
          id: selectedIdToDeleteOrEdit,
        };

        dispatch(editDevice({ payload })).then(() => {
          handleCloseModal();
          onRefetchData();
        });
      } else {
        dispatch(addDevice({ payload: form.values })).then(() => {
          handleCloseModal();
          onRefetchData();
        });
      }
    }
  };

  const [formFilterInitialValues, setFormFilterInitialValues] = useState({
    townCouncil: filter?.townCouncil || "",
    site: filter?.site || "",
    block: filter?.block || "",
    isOnline: filter?.onlineOrOffline || "",
  });

  const modalActionConfig: any = isDelete
    ? [
      {
        label: "Yes",
        event: handleDelete,
        variant: "primary",
      },
    ]
    : [
      {
        label: "Save",
        event: onSubmit,
        variant: "primary",
      },
    ];

  const filterActionConfig: any = {
    label: "Filter",
    event: onSubmitFilter,
    variant: "primary",
  };

  const reset: any = {
    label: "Reset",
    event: onReset,
    variant: "secondary",
  };

  const actionConfig: any = [
    {
      label: "Edit",
      actionProperty: "deviceId", // don't know the parameter
      event: editAction,
      variant: "secondary",
      className: "mx-2 my-1",
      moduleName: "Device",
      actionName: "Edit-Device",
    },
    {
      label: "Decommission",
      actionProperty: "deviceId", // don't know the parameter
      event: deleteAction,
      variant: "danger",
      className: "mx-2 my-1",
      moduleName: "Device",
      actionName: "Decommission-Device",
    },
  ];

  const emptyConfig = {
    title: "No Device Found",
    description: "",
  };

  const filterFormConfig: FilterFormConfig = {
    controls: [
      {
        controlGroup: [
          {
            name: "townCouncil",
            placeholder: "Select Town Council *",
            type: "searchable-select",
            options: tcOptions,
            onChange: onChangeTC,
            required: true,
            isLoading: isLoadingTownCouncils,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "site",
            placeholder: "Select Site",
            type: "searchable-select",
            options: siteOptions,
            onChange: onChangeSite,
            required: false,
            isLoading: isLocationSitesByTCCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "block",
            placeholder: "Select Block",
            type: "searchable-select",
            options: blockOptions,
            onChange: onChangeBlock,
            required: false,
            isLoading: isLoadingBlocksBySiteCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "isOnline",
            placeholder: "Select Online/Offline",
            type: "searchable-select",
            options: onlineOfflineOptions,
            required: true,
            isLoading: false,
          },
        ],
      },
    ],
  };

  const [tempFileUploadResponse, setTempFileUploadResponse] = useState([]);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState<boolean>(false);
  const [fileUploadResponse, setFileUploadResponse] = useState([] as any);
  const [editableRows, setEditableRows] = useState([] as any);
  const [tempEditableRows, setTempEditableRows] = useState([] as any);
  // const [editableDeviceCodes, setEditableDeviceCodes] = useState([] as any);
  // const [isValidation, setIsValidation] = useState<any>(true);
  // const [file, setFile] = useState(null as any);
  // const [modalNumberOfRecords, setmodalNumberOfRecords] = useState(0);
  const [editableDeviceCodes] = useState([] as any);
  const [isValidation] = useState<any>(true);
  const [file] = useState(null as any);
  const [modalNumberOfRecords] = useState(0);
  const [modalPage, setModalPage] = useState(1);
  const [modalPageForEditable, setModalPageForEditable] = useState(1);
  const [filterUpdateCount, setFilterUpdateCount] = useState(0);

  const modalPageSize = 5;

  const paginate = (array: [], page_size: any, page_number: any) => {
    const data = array.slice(
      (page_number - 1) * page_size,
      page_number * page_size
    );
    return data;
  };

  useEffect(() => {
    const data = paginate(fileUploadResponse, modalPageSize, modalPage);
    setTempFileUploadResponse(data);
  }, [modalPage]);

  useEffect(() => {
    const data = paginate(editableRows, modalPageSize, modalPage);
    setTempEditableRows(data);
  }, [modalPageForEditable]);

  const confirmAssetsFromErrorResults = async () => {
    setProgress(0);

    try {
      const selectedItemsData = selectedItems.filter((item: any) => item.value);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("isValidation", isValidation);
      for (let item of selectedItemsData) {
        formData.append("deviceCodesForUpdate[]", item.value);
      }

      await axiosInstance
        .post(`/dmsapp/Device/InsertDeviceByFile`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            if (total !== null && total !== undefined) {
              const percentCompleted = Math.round((loaded * 100) / total);
              setProgress(percentCompleted);
            }
          },
        })
        .then((response: any) => {
          setIsAssetModalOpen(false);
          document.body.classList.remove("loading-indicator");
          onRefetchData();
        })
        .catch((error: any) => {
          setProgress(0);
          console.log("error uploading file: ", error);
        });
    } catch (error) {
      setProgress(0);
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  /* const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
      setProgress(0);

      try {
        const formData = new FormData();
        formData.append("file", event.target.files[0]);
        formData.append("isValidation", isValidation);

        await axiosInstance
          .post(`/dmsapp/Device/InsertDeviceByFile`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const { loaded, total } = progressEvent;
              if (total !== null && total !== undefined) {
                const percentCompleted = Math.round((loaded * 100) / total);
                setProgress(percentCompleted);
              }
            },
          })
          .then((response: any) => {
            if (response?.data?.data?.deviceUploadValidationDetails?.length) {
              setFileUploadResponse(
                _.cloneDeep(response?.data?.data?.deviceUploadValidationDetails)
              );
              setEditableDeviceCodes(
                _.cloneDeep(response?.data?.data?.updateableDeviceCode)
              );

              const data = paginate(
                response?.data?.data?.deviceUploadValidationDetails,
                modalPageSize,
                modalPage
              );

              setTempFileUploadResponse(data);

              setmodalNumberOfRecords(response?.data?.numberOfRecords);

              setIsAssetModalOpen(true);
              setIsValidation(false);
            } else {
              onRefetchData();
            }
          })
          .catch((error: any) => {
            setProgress(0);
            console.log("error uploading file: ", error);
          });
      } catch (error) {
        setProgress(0);
        console.error("Error uploading file:", error);
        throw error;
      }
    }

    event.target.value = "";
  }; */

  useEffect(() => {
    if (fileUploadResponse.length && editableDeviceCodes.length) {
      const editableRows = fileUploadResponse.filter((row: any) =>
        editableDeviceCodes.find((device: any) => device === row.deviceCode)
      );
      setEditableRows(editableRows);
      setTempEditableRows(editableRows);
      const data = paginate(editableRows, modalPageSize, modalPageForEditable);

      setTempEditableRows(data);
    }
  }, [fileUploadResponse]);

  useEffect(() => {
    if (progress > 0 && progress < 100) {
      setShowProgressModal(true);
    } else if (progress === 100) {
      setShowProgressModal(true);

      setTimeout(() => {
        setShowProgressModal(false);
        document.body.classList.add("loading-indicator");
      }, 500);
    } else {
      setShowProgressModal(false);
    }
  }, [progress]);

  useEffect(() => {
    setSiteOptions([]);
    setBlockOptions([]);
  }, []);

  const onModalPageChange = (page: number) => {
    setModalPage(page);
  };

  const onModalPageChangeForEditable = (page: number) => {
    setModalPageForEditable(page);
  };

  const onModalSortChange = (sortBy: string, sortDirection: any) => {
    // console.log("sortBy", sortBy, "sortDirection", sortDirection);
  };

  /*   const onClickDownload = () => {
      dispatch(getDeviceSampleFile());
    }; */

  const [modalColumns] = useState([
    {
      title: "Device ID",
      label: "deviceCode",
      isSorted: true,
    },
    {
      title: "Message",
      label: "message",
    },
  ]);

  const validationSchemaFilter = yup.object().shape({
    townCouncil: yup.string().required("Town Council is required"),
  });

  const { data: deviceStatusCountData, isLoading: isLoadingDeviceStatusCount } =
    useQuery({
      queryKey: ["getDeviceStatusCount", isSubmitedFilter],
      queryFn: ({ signal }) =>
        getDeviceStatusCount(
          selectedTcCode,
          selectedSiteCode,
          selectedBlockCode,
          signal
        ),
    });

  const getDeviceModalHeaderTitle = () => {
    if (isEdit) return "Edit Device";
    if (isDelete) return "Decommission Device";
    return "Add Device";
  };

  return (
    <div>
      <div className="mt-2">
        <TitleComponent title="Overview" />

        <DeviceStatisticsSection
          deviceStatusCountData={deviceStatusCountData}
          isLoadingDeviceStatusCount={isLoadingDeviceStatusCount}
        />
      </div>
      <div>
        <FilterFormComponent
          key={formFilterInitialValues?.townCouncil}
          formRef={formRef}
          initialValues={formFilterInitialValues}
          formConfig={filterFormConfig}
          actionConfig={filterActionConfig}
          resetButton={reset}
          filterUpdateCount={filterUpdateCount}
          validation={validationSchemaFilter}
        />
      </div>
      <div>
        <div className="d-flex justify-content-end table-generic-action  table-toolbar">
          <SearchBarComponent
            placeholder="Search Device ID"
            methodToDispatch={getDMSDeviceList}
            getSearch={onSearch}
          />
          {/* <HasPermission
            moduleName={ADMIN_MODULE}
            functionId={AdmindModuleList.UploadDevices.functionId}
            isRoute={false}
          >
            <B2Button className="icon-text-btn ms-2" variant="primary">
              <label htmlFor="upload" className="me-2 cursor-pointer">
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Import Device
                <input
                  id="upload"
                  type="file"
                  className="d-none"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleFileChange(event);
                  }}
                />
              </label>
            </B2Button>

            <B2Button
              className="ms-2 icon-text-btn"
              variant="primary"
              onClick={() => onClickExportDevice()}
            >
              {isLoadingDeviceExport ? (
                <Spinner
                  animation="border"
                  variant="info"
                  size="sm"
                  className="me-2"
                />
              ) : (
                <FontAwesomeIcon icon={faDownload} className="me-2" />
              )}
              Export Device
            </B2Button>
          </HasPermission> */}
          {/*           <B2Button
            className="ms-2 icon-text-btn"
            variant="primary"
            onClick={() => {
              onClickDownload();
            }}
          >
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Download Sample File
          </B2Button> */}
        </div>

        <div className="my-3">
          <TableComponent
            tableHeaders={columns}
            emptyConfig={emptyConfig}
            data={data}
            totalNumberOfRecords={totalNumberOfRecords}
            pageSize={pageSize}
            current={current}
            onChangePage={onChangePage}
            actionConfig={actionConfig}
            onChangeSort={onChangeSort}
            isLoadingData={isLoading}
          />
        </div>
      </div>

      {/* modals */}

      {isModalOpen && !isEdit && (
        <ModalComponent
          isConformationModal={isDelete}
          showModal={isModalOpen}
          handleClose={handleCloseModal}
          headerTitle={getDeviceModalHeaderTitle()}
          actionConfig={modalActionConfig}
        >
          {isDelete ? (
            <h5 className="text-center">
              Are you sure to <b className="text-danger">Decommission</b>?
            </h5>
          ) : (
            <SkeletonComponent
              wrapperHeight="480px"
              skeletonHeight="50px"
              isLoading={isLoadingDmsDeviceDetails}
              count={8}
            >
              <></>
              {/* <FormBuilderComponent
                formRef={formRef}
                initialValues={initialValues}
                validation={validationSchema}
                formConfig={formConfig}
              /> */}
            </SkeletonComponent>
          )}
        </ModalComponent>
      )}

      {isEdit && (
        <AssetEditTabComponent
          data={dmsDeviceDetails}
          setIsEdit={setIsEdit}
          headerTitle={"Device Edit"}
          setIsModalOpen={setIsModalOpen}
          onRefetchData={onRefetchData}
          isDMSDeviceEdit={true}
          isLoadingData={isLoadingDmsDeviceDetails}
        />
      )}

      {isAssetModalOpen && (
        <AssetUploadModal
          showModal={isAssetModalOpen}
          handleClose={handleCloseModal}
        >
          <div className="ms-3 d-block">
            <div>
              <div style={{ fontSize: "18px", fontWeight: "700" }}>
                <div>
                  The uploaded file has erros with some assets. If you proceed
                  with the file, the assets with proper data will be added
                  whereas assets with errors will not be added to the system.
                  You can cancel for now and have a correction to upload your
                  file once again or can proceed with the current file.
                </div>

                <div style={{ marginTop: "1rem" }}>
                  Click save to continue with the currect file.
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
              className="my-5"
            >
              <B2Button
                variant="primary"
                onClick={() => {
                  void confirmAssetsFromErrorResults();
                }}
              >
                Save
              </B2Button>
            </div>
          </div>

          <div className="custom_tab_start">
            <B2Tabs
              activeKey="Assetswitherror"
              // as="button"
              pills
              tabClassName={""}
              tab={[
                {
                  content: (
                    <div>
                      {!!tempFileUploadResponse?.length && (
                        <TableComponent
                          tableHeaders={modalColumns}
                          emptyConfig={emptyConfig}
                          data={tempFileUploadResponse}
                          totalNumberOfRecords={modalNumberOfRecords}
                          pageSize={modalPageSize}
                          current={modalPage}
                          onChangePage={onModalPageChange}
                          onChangeSort={onModalSortChange}
                        />
                      )}
                    </div>
                  ),
                  id: "Assetswitherror",
                  title: "Assets with error",
                },
                {
                  content: (
                    <div>
                      {!!tempFileUploadResponse?.length && (
                        <TableComponent
                          tableHeaders={modalColumns}
                          emptyConfig={emptyConfig}
                          data={tempEditableRows}
                          totalNumberOfRecords={modalNumberOfRecords}
                          pageSize={modalPageSize}
                          current={modalPageForEditable}
                          onChangePage={onModalPageChangeForEditable}
                          onChangeSort={onModalSortChange}
                          enableSelection={true}
                        />
                      )}
                    </div>
                  ),
                  id: "Editableassetsfromtheerrorlist",
                  title: "Update assets from the error list",
                },
              ]}
            />
          </div>
        </AssetUploadModal>
      )}
      {showProgressModal && (
        <ProgressBarModalComponent
          showModal={showProgressModal}
          progress={progress}
        />
      )}
    </div>
  );
};

export default DMSDeviceTableComponent;
